import { useLocation, useNavigate } from 'react-router-dom';
import { Context } from './Context';
import React, { useState, useEffect, useContext, useRef, useMemo, type ReactNode } from 'react';
import type { Model, Entity, Property, Action } from '../design/types';
import type { Component } from '../design/components';
import type { Reactor, ReactiveEntity } from '../design/services';
import { Text as NativeText } from '../components/native/Text';
import { Text as MuiText } from '../components/mui/Text';
import { Text as ShadcnText } from '../components/shadcn/Text';
import { Button as NativeButton } from '../components/native/Button';
import { Button as MuiButton } from '../components/mui/Button';
import { Button as ShadcnButton } from '../components/shadcn/Button';
import { List as NativeList } from '../components/native/List';
import { List as MuiList } from '../components/mui/List';
import { List as ShadcnList } from '../components/shadcn/List';
import { Switch as NativeSwitch } from '../components/native/Switch';
import { Switch as MuiSwitch } from '../components/mui/Switch';
import { Switch as ShadcnSwitch } from '../components/shadcn/Switch';
import { Code as NativeCode } from '../components/native/Code';
import { Code as MuiCode } from '../components/mui/Code';
import { Code as ShadcnCode } from '../components/shadcn/Code';
import { Icon as NativeIcon } from '../components/native/Icon';
import { Icon as MuiIcon } from '../components/mui/Icon';
import { Icon as ShadcnIcon } from '../components/shadcn/Icon';
import { Geopoint as NativeGeopoint } from '../components/native/Geopoint';
import { Geopoint as MuiGeopoint } from '../components/mui/Geopoint';
import { Geopoint as ShadcnGeopoint } from '../components/shadcn/Geopoint';
import { Color as NativeColor } from '../components/native/Color';
import { Color as MuiColor } from '../components/mui/Color';
import { Color as ShadcnColor } from '../components/shadcn/Color';
import { Pdf as NativePdf } from '../components/native/Pdf';
import { Pdf as MuiPdf } from '../components/mui/Pdf';
import { Pdf as ShadcnPdf } from '../components/shadcn/Pdf';
import { Image as NativeImage } from '../components/native/Image';
import { Image as MuiImage } from '../components/mui/Image';
import { Image as ShadcnImage } from '../components/shadcn/Image';
import { Select as NativeSelect } from '../components/native/Select';
import { Select as MuiSelect } from '../components/mui/Select';
import { Select as ShadcnSelect } from '../components/shadcn/Select';
import { DateTime as NativeDateTime } from '../components/native/DateTime';
import { DateTime as MuiDateTime } from '../components/mui/DateTime';
import { DateTime as ShadcnDateTime } from '../components/shadcn/DateTime';
import { Notice as NativeNotice } from '../components/native/Notice';
import { Notice as MuiNotice } from '../components/mui/Notice';
import { Notice as ShadcnNotice } from '../components/shadcn/Notice';
import type { Context as ContextType, PathSegment } from '../design/app';

/**
 * Component registry - maps property types to component implementations.
 * Uses design setting to select native or MUI components.
 */
const componentRegistry = {
  native: {
    text: NativeText,
    button: NativeButton,
    list: NativeList,
    switch: NativeSwitch,
    code: NativeCode,
    icon: NativeIcon,
    geopoint: NativeGeopoint,
    color: NativeColor,
    pdf: NativePdf,
    image: NativeImage,
    select: NativeSelect,
    datetime: NativeDateTime,
  },
  mui: {
    text: MuiText,
    button: MuiButton,
    list: MuiList,
    switch: MuiSwitch,
    code: MuiCode,
    icon: MuiIcon,
    geopoint: MuiGeopoint,
    color: MuiColor,
    pdf: MuiPdf,
    image: MuiImage,
    select: MuiSelect,
    datetime: MuiDateTime,
  },
  shadcn: {
    text: ShadcnText,
    button: ShadcnButton,
    list: ShadcnList,
    switch: ShadcnSwitch,
    code: ShadcnCode,
    icon: ShadcnIcon,
    geopoint: ShadcnGeopoint,
    color: ShadcnColor,
    pdf: ShadcnPdf,
    image: ShadcnImage,
    select: ShadcnSelect,
    datetime: ShadcnDateTime,
  },
} as const;

type Design = keyof typeof componentRegistry;
type ComponentType = keyof typeof componentRegistry.native;

/**
 * Extract property names referenced in a label code string.
 * Handles both direct access (this.prop) and array iteration (.map(x => x.prop)).
 */
function extractLabelProps(label: string): Set<string> {
  const props = new Set<string>();

  // Pattern 1: this.propName or this.propName? (direct property access)
  for (const match of label.matchAll(/this\.(\w+)\??/g)) {
    props.add(match[1]);
  }

  // Pattern 2: .map(x => x.propName) or .map(x => x.propName?) (array iteration)
  // Matches: .map(varName => varName.property) with optional chaining
  for (const match of label.matchAll(/\.map\s*\(\s*(\w+)\s*=>\s*\1\.(\w+)\??/g)) {
    props.add(match[2]);
  }

  return props;
}

/**
 * Build populate paths needed for evaluating reference labels.
 *
 * @param model - The model to build paths for
 * @param deep - If true, includes all nested reference properties that have labels (for item view).
 *               If false, only includes references needed for the model's own label (for list view).
 *
 * List view (deep=false): Only populate what's needed for row labels
 * Item view (deep=true): Populate all reference properties that have labels for display
 *
 * TODO: Deeper nesting requires a more complex recursive approach.
 * Circular references are not handled. Array indexing (array[0]) not yet supported.
 */
function buildPopulatePaths(model: Model, deep = false): string[] {
  const paths: string[] = [];

  for (const prop of model.properties) {
    if (!((prop.type === 'reference' || (prop.type === 'object' && prop.reference)) && prop.reference)) continue;

    const path = prop.name;
    paths.push(path);

    const referencedModel = prop.reference;

    // Check the referenced model's label for any nested references it depends on
    const label = referencedModel.label;
    if (label) {
      const propNames = extractLabelProps(label);
      for (const refPropName of propNames) {
        const refProp = referencedModel.properties.find((p: Property) => p.name === refPropName);
        if (refProp?.type === 'reference' && refProp.reference) {
          paths.push(`${path}.${refPropName}`);
          addNestedLabelPaths(paths, path, refPropName, refProp.reference);
        }
      }
    }

    // For item view, also check all reference properties in the referenced model that have labels
    // These need to be populated so their labels can be evaluated for display in forms
    if (deep) {
      for (const nestedProp of referencedModel.properties) {
        if (nestedProp.type !== 'reference' || !nestedProp.reference) continue;
        if (!nestedProp.reference.label) continue;

        const nestedPath = `${path}.${nestedProp.name}`;
        if (!paths.includes(nestedPath)) {
          paths.push(nestedPath);
          addNestedLabelPaths(paths, path, nestedProp.name, nestedProp.reference);
        }
      }
    }
  }

  return paths;
}

/**
 * Add paths for nested references found in a model's label (one more level deep).
 */
function addNestedLabelPaths(
  paths: string[],
  basePath: string,
  propName: string,
  refModel: Model
): void {
  const nestedLabel = refModel.label;
  if (!nestedLabel) return;

  const nestedPropNames = extractLabelProps(nestedLabel);
  for (const nestedPropName of nestedPropNames) {
    const nestedProp = refModel.properties.find((p: Property) => p.name === nestedPropName);
    if (nestedProp?.type === 'reference') {
      const nestedPath = `${basePath}.${propName}.${nestedPropName}`;
      if (!paths.includes(nestedPath)) {
        paths.push(nestedPath);
      }
    }
  }
}

/**
 * Maps property.type to component registry key.
 * Returns the component type to use for rendering.
 */
function getComponentType(property: Property | undefined): ComponentType {
  if (!property) return 'text';
  if (property.enum) return 'select';

  switch (property.type) {
    case 'boolean': return 'switch';
    case 'code': return 'code';
    case 'icon': return 'icon';
    case 'geopoint': return 'geopoint';
    case 'color': return 'color';
    case 'pdf': return 'pdf';
    case 'image': return 'image';
    case 'date': return 'datetime';
    case 'date-time': return 'datetime';
    case 'time': return 'datetime';
    case 'month': return 'datetime';
    case 'year': return 'datetime';
    default: return 'text';
  }
}

/**
 * getComponent - Factory function that returns the appropriate JSX element.
 * Uses the component registry to select native or MUI components based on design setting.
 * Renders object/reference properties with nested properties as List components.
 */
function getComponent(
  component: Component,
  property: Property | undefined,
  design: Design,
  reactor: Reactor,
  referenceOptions?: Record<string, Entity[]>,
  editingRefs?: Set<string>,
  setEditingRefs?: React.Dispatch<React.SetStateAction<Set<string>>>,
  onNavigate?: (path: string) => void,
  basePath?: string
): ReactNode {
  if (component.hidden) return null;

  const registry = componentRegistry[design];

  // Object or reference type with nested properties renders as a List
  const nestedModel = property?.type === 'object'
    ? (property.reference ?? { name: property.name, properties: property.properties } as Model)
    : property?.type === 'reference'
    ? property.reference
    : undefined;

  // Object+reference: render navigable list (items live in their own model's collection)
  if (nestedModel && property?.type === 'object' && property.reference && onNavigate && basePath) {
    const List = registry.list;
    const itemComponents = (component.state?.[0] ?? []) as Component[][];
    return (
      <div key={property.name} style={{ marginBottom: '1rem' }}>
        <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
          {property.description || property.name}
        </label>
        <List
          properties={nestedModel.properties}
          state={[itemComponents, () => {}]}
          readonly={component.readonly}
          onRowClick={(rowIndex: number) => {
            const childId = itemComponents[rowIndex]?.find(c => c.name === 'id')?.state?.[0];
            if (childId) onNavigate(`${basePath}/${property.name}/${childId}`);
          }}
          action={['Add', () => onNavigate(`${basePath}/${property.name}/new`)]}
        />
      </div>
    );
  }

  if (nestedModel) {
    const List = registry.list;
    // state[0] is Component[][] (pre-created by Reactor)
    const itemComponents = (component.state?.[0] ?? []) as Component[][];
    const setValue = component.state?.[1] || (() => {});

    // For reference types, check if currently in edit mode
    const isEditing = property?.type === 'reference' && editingRefs?.has(property.name);
    const optionEntities = isEditing && referenceOptions
      ? referenceOptions[property!.name] as Entity[]
      : undefined;

    // Create Component[][] for options using Reactor service
    let optionComponents: Component[][] | undefined;
    if (optionEntities) {
      reactor.sustain(nestedModel, optionEntities, (optionREs) => {
        optionComponents = optionREs
          .filter(re => re.entity)
          .map(re => [
            { name: 'id', state: [re.entity!.id, () => {}] } as Component,
            ...re.components
          ]);
      });
    }

    // Derive selection limits from property
    const limits: [number, number] | undefined = property?.type === 'reference'
      ? property.array
        ? [component.required ? 1 : 0, Infinity]
        : [component.required ? 1 : 0, 1]
      : undefined;

    // Toggle action for reference fields
    const action: [string, () => void] | undefined = property?.type === 'reference' && setEditingRefs
      ? [
          isEditing ? 'Done' : 'Select',
          () => {
            setEditingRefs(prev => {
              const next = new Set(prev);
              if (isEditing) {
                next.delete(property!.name);
              } else {
                next.add(property!.name);
              }
              return next;
            });
          }
        ]
      : undefined;

    // Handle selection changes - update state with selected Component[][]
    const handleSelectionChange = (newValue: Component[] | Component[][] | null) => {
      if (!newValue) {
        setValue(null);
        return;
      }
      const isSingleSelect = limits && limits[1] === 1;
      if (isSingleSelect) {
        // Single select: newValue is Component[], wrap in array
        setValue([newValue as Component[]]);
      } else {
        setValue(newValue as Component[][]);
      }
    };

    return (
      <div key={property!.name}>
        <label style={{ fontSize: '0.875rem', fontWeight: 500, display: 'block', marginBottom: '0.25rem' }}>
          {property!.name}
        </label>
        <List
          properties={nestedModel.properties}
          state={[itemComponents, handleSelectionChange]}
          options={optionComponents}
          limits={limits}
          readonly={component.readonly}
          action={action}
        />
      </div>
    );
  }

  // Get component from registry based on property type
  const componentType = getComponentType(property);
  const ComponentToRender = registry[componentType] as React.FC<Component & Record<string, unknown>>;

  // Add type-specific props
  const typeProps: Record<string, unknown> = {};
  if (property?.multiline) {
    typeProps.multiline = property.multiline;
  }
  // DatePicker variant derived from property type
  if (componentType === 'datetime' && property) {
    const variantMap: Record<string, string> = {
      'date': 'date', 'date-time': 'date-time', 'time': 'time',
      'month': 'month', 'year': 'year',
    };
    typeProps.variant = variantMap[property.type] || 'date';
    if (property.range) typeProps.range = true;
  }
  if (property?.enum) {
    try {
      const values = new Function(property.enum)() as (string | number)[];
      const isNumeric = values.length > 0 && typeof values[0] === 'number';
      typeProps.options = values.map(v => ({ state: [String(v), () => {}], label: String(v) }));
      typeProps.clearable = !component.required;
      if (isNumeric) {
        const [val, setVal] = component.state || ['', () => {}];
        typeProps.state = [val != null ? String(val) : '', (v: string) => setVal(v === '' ? '' : Number(v))];
      }
    } catch (err) { console.error(`Failed to evaluate enum for ${property.name}:`, err); }
  }

  return <ComponentToRender key={component.name} {...component} {...typeProps} />;
}

function CustomPropertyView({ code, ...props }: { code: string } & Record<string, unknown>) {
  const Component = React.useMemo(() => {
    try { return new Function('return ' + code)(); }
    catch (err) { return () => <div style={{ color: 'red', fontSize: '12px' }}>Error: {err instanceof Error ? err.message : String(err)}</div>; }
  }, [code]);
  return React.createElement(Component, props);
}

/**
 * ComponentView - Renders a form for a single entity.
 * Object/reference properties with nested properties render as List components.
 */
function ComponentView({ components, model, design, onAction, runningAction, referenceOptions, editingRefs, setEditingRefs, entityId, entityData, reactor, context, navigate, basePath }: {
  components: Component[];
  model: Model;
  design: Design;
  onAction: (action: Action) => void;
  runningAction?: string | null;
  referenceOptions?: Record<string, Entity[]>;
  editingRefs?: Set<string>;
  setEditingRefs?: React.Dispatch<React.SetStateAction<Set<string>>>;
  entityId?: string;
  entityData?: Entity | null;
  reactor: Reactor;
  context: ContextType;
  navigate: (path: string) => void;
  basePath?: string;
}): ReactNode {
  const Button = componentRegistry[design].button;

  // Filter and render actions using Reactor-evaluated __action__ components
  const visibleActions = model.actions.filter(action => {
    const ac = components.find(c => c.name === `__action__${action.name}`);
    return ac ? !ac.hidden : true;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      {components.map(c => {
        const property = model.properties.find(p => p.name === c.name);
        if (!property) return null;  // Skip synthetic components (id, __label__, __filter__, __action__)

        // Custom properties: evaluate code at Page level
        if (property?.type === 'custom') {
          if (c.hidden) return null;
          const code = c.state?.[0] as string;
          if (!code) return null;
          const data: Record<string, unknown> = entityData ?? {};
          const registry = componentRegistry[design];
          return <CustomPropertyView key={c.name} code={code} React={React} app={context} navigate={navigate} model={model} data={data} id={entityId} components={registry} />;
        }

        return getComponent(c, property, design, reactor, referenceOptions, editingRefs, setEditingRefs, navigate, basePath);
      })}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
        {visibleActions.map(action => {
          const ac = components.find(c => c.name === `__action__${action.name}`);
          return (
            <Button
              key={action.name}
              name={action.name}
              description={action.description}
              disabled={ac?.disabled || !!runningAction}
              loading={runningAction === action.name}
              onClick={() => onAction(action)}
            />
          );
        })}
      </div>
    </div>
  );
}

type ListViewProps = {
  components: Component[][];
  model: Model;
  design: Design;
  onRowClick?: (rowIndex: number) => void;
  action?: [string, () => void];
}

/**
 * ListView - Renders a table of entities.
 */
function ListView({ components, model, design, onRowClick, action }: ListViewProps): ReactNode {
  const List = componentRegistry[design].list;

  return (
    <List
      name={model.name}
      properties={model.properties}
      state={[components, () => {}]}
      onRowClick={onRowClick}
      action={action}
      view={model.view}
    />
  );
}


function resolveNestedPath(
  rootModel: Model, rootId: string,
  segs: { propertyName: string; itemId: string }[]
): { model: Model; path: PathSegment[] } | null {
  const path: PathSegment[] = [{ model: rootModel, id: rootId, new: false }];
  let m = rootModel;
  for (const seg of segs) {
    const prop = m.properties.find(p => p.name === seg.propertyName);
    if (!prop || prop.type !== 'object' || !prop.reference) return null;
    m = prop.reference as Model;
    path.push({ model: m, id: seg.itemId, new: false });
  }
  return { model: m, path };
}

export default function Page() {
  const location = useLocation();
  const navigate = useNavigate();
  const [context] = useContext(Context)!;
  const segments = location.pathname.split('/').filter(Boolean);

  const modelPath = segments[0] || '';
  const rootId = segments[1] || '';
  const nestedSegments: { propertyName: string; itemId: string }[] = [];
  for (let i = 2; i < segments.length; i += 2) {
    nestedSegments.push({ propertyName: segments[i], itemId: segments[i + 1] || '' });
  }

  const [model, setModel] = useState<Model | null>(null);
  const [reactiveEntities, setReactiveEntities] = useState<ReactiveEntity[]>([]);
  const [referenceOptions, setReferenceOptions] = useState<Record<string, Entity[]>>({});
  const [editingRefs, setEditingRefs] = useState<Set<string>>(new Set());

  // Derive active model/id/path from nested URL segments
  const resolved = (model && nestedSegments.length > 0)
    ? resolveNestedPath(model, rootId, nestedSegments) : null;
  // null when URL has an invalid property name (e.g. /courses/id/nonexistent/id2)
  const activeModel = (nestedSegments.length > 0 && !resolved) ? null : (resolved?.model ?? model);
  const activeId = nestedSegments.length > 0
    ? nestedSegments[nestedSegments.length - 1].itemId : rootId;
  const path: PathSegment[] = resolved?.path ?? (model ? [{ model, id: rootId, new: false }] : []);

  // Tracks whether the current entity was seeded from the real DB (existing entity)
  // vs created fresh in staging (new entity). Used by effect 3 cleanup to decide
  // whether to discard staging on navigate away.
  const seededFromRealRef = useRef(false);
  const newEntityRef = useRef(false);   // Signals effect 3 that this is a new entity from effect 2b
  const prevRootIdRef = useRef<string>('');

  // Ref for reading latest reactiveEntities without depending on the reference
  const reactiveEntitiesRef = useRef(reactiveEntities);
  reactiveEntitiesRef.current = reactiveEntities;

  // Stable key that only changes when Reactor-evaluated __filter__ values change.
  // Reads from first entity since all entities share the same model's filter components.
  // Recomputes on every reactiveEntities update but the stringified output is compared
  // by value in the effect deps, so the effect only re-runs on actual filter changes.
  const filterDataKey = useMemo(() => {
    const entity = reactiveEntities[0];
    if (!model || !entity) return '';
    const filters: Record<string, unknown> = {};
    for (const c of entity.components) {
      if (c.name?.startsWith('__filter__')) filters[c.name] = c.state?.[0];
    }
    return Object.keys(filters).length > 0 ? JSON.stringify(filters) : '';
  }, [model, reactiveEntities]);

  // Update page ref (doesn't trigger re-renders).
  // Spreads existing page.current to preserve the `new` and `path` flags set in effect 3 (loadEntities).
  // The `new`/`path` flags are set once before Reactor.sustain() and must survive subsequent re-renders.
  useEffect(() => {
    if (reactiveEntities.length > 0) {
      context.page.current = { ...context.page.current!, reactiveEntities, model, navigate };
    }
  }, [reactiveEntities, context.page, model, navigate]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runningAction, setRunningAction] = useState<string | null>(null);

  // 1. Load model definition from context.authn
  useEffect(() => {
    setLoading(true);
    setError(null);

    // Find model from authorized capabilities (match by path, prefer current project)
    const targetPath = `/${modelPath}`;
    const auth = context.authn.find(a => a.capability?.model?.path === targetPath && a.project?.id === context.project?.id);
    if (auth?.capability?.model && !auth.capability.model.hidden) {
      setModel(auth.capability.model as Model);
    } else if (context.authn.length === 0) {
      // Auth chain still loading — show spinner, not error
      setModel(null);
      setError(null);
      setLoading(true);
      return;
    } else {
      setModel(null);
      setError(modelPath ? `Model '${modelPath}' not found` : null);
    }
    setLoading(false);
  }, [modelPath, context.authn, context.project]);

  // 2. Load reference options for edit mode (single entity view only)
  useEffect(() => {
    if (!activeModel || (activeModel.type === 'collection' && !activeId)) {
      setReferenceOptions({});
      return;
    }

    let stale = false;

    async function loadReferenceOptions() {
      const options: Record<string, Entity[]> = {};

      const currentEntities = reactiveEntitiesRef.current;

      for (const prop of activeModel!.properties) {
        if (prop.type === 'reference' && prop.reference) {
          try {
            const populatePaths = buildPopulatePaths(prop.reference, false);
            // Read filter from Reactor's synthetic __filter__ component
            let propFilter: Record<string, unknown> | undefined;
            if (prop.filter) {
              const filterComp = currentEntities[0]?.components.find(
                c => c.name === `__filter__${prop.name}`
              );
              const filterValue = filterComp?.state?.[0] as Record<string, unknown> | undefined;
              propFilter = filterValue && typeof filterValue === 'object' && !Array.isArray(filterValue)
                ? filterValue
                : { id: '' }; // fail closed
            }
            const entities = await context.database.list(prop.reference, populatePaths, propFilter);
            if (stale) return;
            options[prop.name] = entities as Entity[];
          } catch (err) {
            if (stale) return;
            console.error(`Failed to load options for ${prop.name}:`, err);
            options[prop.name] = [];
          }
        }
      }

      setReferenceOptions(options);
    }

    loadReferenceOptions();
    return () => { stale = true; };
  }, [activeModel, activeId, context.database, filterDataKey]);

  // 2b. Handle 'new' — create empty staging entity, generate UUID and redirect.
  // Defaults are evaluated by the Reactor in effect 3 (Phase 2) when it sees null entity.
  useEffect(() => {
    if (!activeModel || activeId !== 'new') return;
    const dbModel = activeModel.type === 'personal' && activeModel.reference ? activeModel.reference as Model : activeModel;
    newEntityRef.current = true;
    (async () => {
      const newId = crypto.randomUUID();
      await context.database.create(dbModel, {}, newId, true);
      // For nested new, append child ID to parent's property array in staging
      if (nestedSegments.length > 0) {
        const parentProp = nestedSegments[nestedSegments.length - 1].propertyName;
        const parentId = nestedSegments.length === 1
          ? rootId : nestedSegments[nestedSegments.length - 2].itemId;
        const parentModel = nestedSegments.length === 1
          ? (model!.type === 'personal' && model!.reference ? model!.reference as Model : model!)
          : resolveNestedPath(model!, rootId, nestedSegments.slice(0, -1))!.model;
        const parent = await context.database.get(parentModel, parentId);
        const existing = (parent[parentProp] as string[]) || [];
        await context.database.update(parentModel, parentId,
          { [parentProp]: [...existing, newId] }, true);
      }
      navigate(location.pathname.replace(/\/new$/, '') + `/${newId}`, { replace: true });
    })();
  }, [activeModel, activeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 2c. Seed existing entity to staging for editing.
  // For existing entities, copies real data into staging so edits don't touch the real DB.
  // Sets seededFromRealRef=true on success so effect 3 cleanup knows to discard staging on navigate away.
  // For new entities (created via effect 2b), the create() call throws (already in staging),
  // so seededFromRealRef stays false — their staging persists across navigation.
  useEffect(() => {
    if (!activeModel || !activeId || activeId === 'new') return;
    const isListView = activeModel.type === 'collection' && !activeId;
    if (isListView) return;
    const dbModel = activeModel.type === 'personal' && activeModel.reference ? activeModel.reference as Model : activeModel;
    (async () => {
      try {
        const entity = await context.database.get(dbModel, activeId);
        try {
          await context.database.create(dbModel, structuredClone(entity), entity.id, true);
          seededFromRealRef.current = true;
        } catch { /* already seeded */ }
      } catch { /* not found — new entity in staging */ }
    })();
  }, [activeModel, activeId, context.database]);

  // 3. Load entity/entities and create ReactiveEntities via Reactor service
  useEffect(() => {
    if (!activeModel) {
      setReactiveEntities([]);
      return;
    }

    // Clear stale data immediately when model/id changes to prevent showing old entities
    setReactiveEntities([]);
    let stale = false;

    async function loadEntities() {
      const personal = activeModel!.type === 'personal';
      const isListView = activeModel!.type === 'collection' && !activeId;
      const dbModel = personal && activeModel!.reference ? activeModel!.reference as Model : activeModel!;
      const populatePaths = buildPopulatePaths(activeModel!, !isListView);
      const isSingleEntity = !isListView && activeId && activeId !== 'new';

      try {
        // Verify nested entity ownership: parent must contain child ID in its property array
        if (nestedSegments.length > 0 && activeId && activeId !== 'new') {
          const rootDbModel = model!.type === 'personal' && model!.reference ? model!.reference as Model : model!;
          const parentDbModel = nestedSegments.length === 1
            ? rootDbModel
            : resolveNestedPath(model!, rootId, nestedSegments.slice(0, -1))!.model;
          const parentId = nestedSegments.length === 1
            ? rootId : nestedSegments[nestedSegments.length - 2].itemId;
          const parentProp = nestedSegments[nestedSegments.length - 1].propertyName;
          const parent = await context.database.get(parentDbModel, parentId);
          const childIds = parent[parentProp];
          const owns = Array.isArray(childIds)
            ? childIds.some((c: unknown) => (typeof c === 'object' && c !== null ? (c as Entity).id : c) === activeId)
            : (typeof childIds === 'object' && childIds !== null ? (childIds as Entity).id : childIds) === activeId;
          if (!owns) {
            throw new Error(`Record with id '${activeId}' not found in ${parentDbModel.name}/${parentId}/${parentProp}`);
          }
        }

        let entities: (Entity | null)[];
        if (personal) {
          if (dbModel.path === '/users') {
            // dbModel is the Users model itself — load by current user's ID
            entities = [await context.database.get(dbModel, context.user!.id, populatePaths)];
          } else {
            // Find the property on dbModel that references the User model
            const userProp = dbModel.properties.find(
              (p: Property) => p.type === 'reference' && (p.reference as Model)?.path === '/users'
            );
            if (userProp) {
              // Collection has a user reference — query by it
              const results = await context.database.list(dbModel, populatePaths,
                { [userProp.name]: context.user!.id }
              );
              if (results.length > 0) {
                entities = [results[0]];
              } else {
                // TODO: Remove stub once Property.default is implemented — the user reference property
                // will get its default value from the Reactor (e.g., default: 'return this.user' on context)
                entities = [{ id: crypto.randomUUID(), [userProp.name]: context.user } as Entity];
              }
            } else {
              throw new Error(`Personal model '${activeModel!.name}' references '${dbModel.name}' which has no user property`);
            }
          }
        } else if (isListView) {
          entities = await context.database.list(dbModel, populatePaths);
        } else if (activeId === 'new') {
          return; // Effect 2b handles this
        } else if (activeId) {
          entities = [await context.database.get(dbModel, activeId, populatePaths)];
        } else {
          entities = [null];
        }

        if (stale) return;

        // Set page state BEFORE Reactor.sustain() so action hidden/disabled code strings
        // can read `context.page.current.path.at(-1)?.new` during initial evaluation.
        const isNewEntity = isSingleEntity && newEntityRef.current;
        newEntityRef.current = false;
        // Preserve `new` flag across navigations: inherit from previous path by (model, id) match.
        // Once any ancestor is new, all descendants are new (unsaved parent → unsaved children).
        const prevPath = context.page.current?.path ?? [];
        let ancestorNew = false;
        const activePath = path.map((seg, i) => {
          if (ancestorNew) return { ...seg, new: true };
          const prevNew = prevPath.find(p => p.model.name === seg.model.name && p.id === seg.id)?.new ?? false;
          const isNew = i === path.length - 1
            ? (!!isNewEntity || prevNew)
            : prevNew;
          if (isNew) ancestorNew = true;
          return { ...seg, new: isNew };
        });
        context.page.current = { reactiveEntities: reactiveEntitiesRef.current, model: activeModel!, navigate, path: activePath };

        const callback = isSingleEntity
          ? (res: ReactiveEntity[]) => {
              setReactiveEntities(res);
              const re = res[0];
              if (!re?.entity) return;
              const { id: _id, ...data } = re.entity;
              context.database.update(dbModel, activeId!, data, true);
            }
          : setReactiveEntities;

        context.reactor.sustain(activeModel!, entities as (Entity | null)[], callback);
      } catch (err) {
        if (stale) return;
        console.error('Error loading entities:', err);
        setError(err instanceof Error ? err.message : 'Failed to load entities');
      }
    }

    loadEntities();
    // Cleanup: discard staging copy when navigating away from the root entity.
    // Only fires when the root entity changes, not when drilling into/out of children.
    return () => {
      stale = true;
      const leavingRoot = prevRootIdRef.current !== '' && prevRootIdRef.current !== rootId;
      if (leavingRoot && seededFromRealRef.current && prevRootIdRef.current !== 'new') {
        const dbM = (model!.type === 'personal' && model!.reference ? model!.reference : model!) as Model;
        context.database.delete(dbM, prevRootIdRef.current, true).catch(() => {});
        // Cascade-discard children
        for (const prop of model!.properties) {
          if (prop.type === 'object' && prop.reference) {
            const childIds = reactiveEntitiesRef.current[0]?.entity?.[prop.name];
            if (Array.isArray(childIds)) {
              for (const cid of childIds) {
                context.database.delete(prop.reference as Model,
                  typeof cid === 'object' ? (cid as Entity).id : cid, true).catch(() => {});
              }
            }
          }
        }
        seededFromRealRef.current = false;
      }
      prevRootIdRef.current = rootId;
    };
    // activeModel/activeId are derived from model + nestedSegments + path + rootId,
    // so listing those as deps would duplicate triggers without changing behavior.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModel, activeId, context.user, context.database, context.reactor]);

  const design = context.project?.design ?? 'native';
  const Notice = design === 'mui' ? MuiNotice : design === 'shadcn' ? ShadcnNotice : NativeNotice;

  if (loading) {
    return <Notice />;
  }

  if (error) {
    return <Notice tone="unknown" detail={error} />;
  }

  if (!model) {
    return null;
  }

  if (!activeModel) { // URL has invalid nested property name (e.g. /courses/id/nonexistent/id2)
    let parentName = model.name;
    nestedSegments.find((seg, i) => {
      const parent = i === 0 ? model : (resolveNestedPath(model, rootId, nestedSegments.slice(0, i))?.model ?? null);
      if (parent) parentName = parent.name;
      return !parent?.properties.find(p => p.name === seg.propertyName && p.type === 'object');
    });
    return <Notice tone="unknown" detail={`Property not found on model '${parentName}'`} />;
  }

  const personal = activeModel.type === 'personal';
  const isListView = activeModel.type === 'collection' && !activeId;
  const dbModel = personal && activeModel.reference ? activeModel.reference as Model : activeModel;

  const executeAction = async (action: Action, reactiveEntity: ReactiveEntity) => {
    const { id: entityId, ...data } = reactiveEntity.entity ?? {} as Entity;
    setRunningAction(action.name);
    try {
      await context.runner.run(action, context as ContextType, {
        model: dbModel,
        id: entityId,
        data,
        navigate
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setRunningAction(null);
    }
  };

  // Compute basePath for nested navigation (e.g., /orders/123)
  const basePath = location.pathname.replace(/\/$/, '');

  if (reactiveEntities.length === 0 && !isListView) {
    return <Notice />;
  }

  return (
    <div>
      {!isListView && (
        <h1 className="text-2xl font-semibold tracking-tight pb-4">
          {(reactiveEntities[0].components.find(c => c.name === '__label__')?.state?.[0] as string) || activeModel.singular || activeModel.name}
        </h1>
      )}
      {isListView ? (
        <ListView
          components={reactiveEntities
            .filter(re => re.entity)
            .map(re => re.components)}
          model={activeModel}
          design={design}
          onRowClick={(rowIndex) => {
            const filteredEntities = reactiveEntities.filter(re => re.entity);
            const id = filteredEntities[rowIndex]?.entity?.id;
            if (id) navigate(`${activeModel.path}/${id}`);
          }}
          action={['Add', () => navigate(`${activeModel.path}/new`)]}
        />
      ) : (
        <ComponentView
          components={reactiveEntities[0].components}
          model={activeModel}
          design={design}
          onAction={(action) => executeAction(action, reactiveEntities[0])}
          runningAction={runningAction}
          referenceOptions={referenceOptions}
          editingRefs={editingRefs}
          setEditingRefs={setEditingRefs}
          entityId={reactiveEntities[0].entity?.id}
          entityData={reactiveEntities[0].entity}
          reactor={context.reactor}
          context={context as ContextType}
          navigate={navigate}
          basePath={basePath}
        />
      )}
    </div>
  );
}
